import type { ReactNode } from 'react';

import { ltr, num } from '../format';

/**
 * The two landing bands under the level list: who built it, and why any of
 * it should be believed (components/home/Credibility.tsx).
 *
 * See web/src/lib/strings/index.ts for how this file is shaped and why, and
 * docs/i18n.md for the Arabic glossary every value here must follow. Neither
 * object may be `as const`: `ar` is typed against `en`, and literal types
 * would make it have to repeat the English words.
 *
 * ## This is the one place the product is selling
 *
 * Everywhere else the voice is an instructor's: short, second person,
 * unimpressed with itself. Here it is an argument aimed at a stranger who has
 * not decided to trust anybody yet, and the Arabic has to make the same
 * argument with the same confidence. Nothing is softened into politeness on
 * the way across — a claim that hedges in one language and not the other is
 * two different products.
 *
 * ## The backticks in `claims`
 *
 * `withCode` in the component splits a body on backticks and sets the odd
 * parts as `<code>`. So the Arabic keeps the same number of backticks, the
 * same contents between them — they are commands the reader will type — and
 * puts the pair wherever the Arabic sentence wants it, which is usually not
 * where the English had it.
 *
 * ## What is not here
 *
 * `ROSTER` is eleven company names and stays in the component: names are not
 * translated. Neither is `~/team/prior-work`, which is a path.
 */

export const en = {
  /* The roster band: the one claim on this page a reader cannot check. */
  pedigree: {
    label: 'Who built it',
    title: 'Built by engineers who have done the job.',
    lede:
      'EngineerOS was written by working software engineers — people who have shipped production systems at the ' +
      'companies below, and who have watched too many good juniors arrive never having checked an agent’s ' +
      'work. Every level is something one of us had to learn on the job.',
    /* The window's own meta line. Counted from the roster, so a name added
       or removed never leaves a number behind that contradicts the list. */
    companies: (n: number) => `${num(n, 'en')} ${n === 1 ? 'company' : 'companies'}`,
    rosterLabel: 'Companies the team has worked at',
    note: 'Names of employers, not endorsements. None of these companies is affiliated with EngineerOS.',
  },

  /* The four numbers that open the proof (components/home/Credibility.tsx) —
     the two-second version of the pitch. Terms and values together, because the value is a translation
     decision too: the digits stay Western in both languages (lib/format) and
     `s` is a unit symbol rather than a word, the same call `MB` gets.

     ## The timing figure, and why it is the cold one

     This read "Boot to a prompt: <2s" and that was the wrong number under a
     heading that promises facts. The repository has two measurements
     (docs/browser.md, phase 0 and phases 2-3), and they are measurements of
     different things:

       restore from a saved state -> agent answering   1.7 s best, 1.7-2.3 s
       FIRST VISIT to a playable prompt, cold cache    10 s

     A stranger on the landing page has a cold cache by definition. They read
     "boot to a prompt" as the time between pressing the button and typing,
     which is the second row, and the first row is what the page was quoting.
     The gap is five-fold and it is the easiest claim on this page to
     disprove — the visitor disproves it by accident, on their way in.

     So the term names the visit rather than the boot, and the value is the
     cold figure. Both numbers come from one busy Windows workstation and
     neither has been taken on a low-end laptop; that is an open item in the
     README, and until it closes the honest move is to quote the slower of the
     two rather than the faster. If the low-end measurement ever lands and is
     worse than this, this number moves again.

     ## It moved: 10 s was a local number (2026-09-27)

     The 10 s was taken against a local origin, where 28 MB arrives in no
     time. Measured on the live site from a real connection, a first visit
     took 23.5-31.5 s, and throttled to 20 Mbit/s 26.6 s. Most of that was the
     download, and 13.7 MB of it was a kernel and initramfs the restore never
     used (packages/vm/src/boot.ts, COLD_BOOT_ASSETS). Without them, at the
     same 20 Mbit/s, 17.2 s; at 8 Mbit/s 30.5 s against 50.5. So the figure is
     ~20s: the slower side of a real connection, not the best case of a local
     one. */
  strip: {
    label: 'What this is',
    figures: (levels: number, worlds: number): Array<{ term: string; value: string }> => [
      { term: 'Levels', value: num(levels, 'en') },
      { term: 'Chapters', value: num(worlds, 'en') },
      { term: 'Servers per learner', value: num(0, 'en') },
      { term: 'First visit to a prompt', value: '~20s' },
    ],
  },

  /* The four claims, in the order an engineer would poke at them. */
  proof: {
    label: 'Why you can trust it',
    title: 'Credibility you can check yourself.',
    /* `uname -a` is an element, so the sentence is parts: the command sits
       at the end of the English clause and inside the Arabic one. */
    lede: (command: ReactNode) => [
      'Everything below is a fact about how this is built, not a promise about how it feels. Open Level 0 and check ' +
        'any of it — ',
      command,
      ' is a fair first command.',
    ],
    claims: {
      linux: {
        title: 'A real Linux, in your tab.',
        body:
          'Alpine on x86, emulated by v86 in WebAssembly. Your bash, your git and your `node --test` all run inside ' +
          'it. There is no scripted terminal and no simulated version control, so a command works here for the same ' +
          'reason it works in a shell — including the ones we never thought of.',
      },
      graded: {
        title: 'Graded on the state you leave.',
        body:
          'The grader reads the repository, never your keystrokes. `git reset --hard HEAD@{1}`, a cherry-pick and a ' +
          'merge all clear the recovery level, because all three leave the repo correct — and retyping the file by ' +
          'hand does not. There is no expected command to guess at. So on the levels, use any tool you like, AI ' +
          'included: you are graded on what you leave behind, not on how you got there. A checkpoint says a model ' +
          'failed it only once that has been measured — the model working alone, three times, failing every time — ' +
          'and then it names the model, its exact version and the day.',
      },
      tested: {
        title: 'Levels are tested like software.',
        body:
          'Every level ships with a reference solution and the wrong solutions people actually try. The suite proves ' +
          'each seed is solvable but not pre-solved, that the solution passes, and that each wrong answer fails ' +
          'exactly the objectives it claims to. A broken level is a red build.',
      },
      /* Every sentence here is a fact about the deployment, checked on
         2026-10-18, and the second one is a fact about a switch.

         Iris is designed (docs/iris.md, "Scripted levels"): her proposals are
         written into each level, and the model behind the unscripted levels
         and the mentor is switched off in production (`IRIS_ENABLED` and
         `MENTOR_ENABLED` are "off" in wrangler.toml), so nothing a learner
         types reaches an AI service. Whoever switches either of them on must
         rewrite this claim in all four languages in the same commit.

         The account sentence is `TRY_THROUGH` (lib/entitlement.ts): levels 0
         to 4 play signed out, so an account is needed after the first five.
         The upload is the checkpoint's independent verification
         (docs/checkpoints.md). */
      nothing: {
        title: 'Nothing to install. Your workspace runs locally.',
        body:
          'Your terminal, repository, grading and Iris all run inside your browser. Iris is designed: she proposes ' +
          'commands written for each level, and nothing you type is sent to an AI service. An account is needed ' +
          'after the first five levels, and a checkpoint you submit for verification uploads that workspace.',
      },
    },
    ctaNote: 'Level 0 is four minutes at a terminal. No sign-up, no card, nothing to install.',
  },
};

export const ar: typeof en = {
  pedigree: {
    label: 'من بناه',
    title: 'بناه مهندسون مارسوا العمل نفسه.',
    lede:
      'بنى EngineerOS مهندسو برمجيات مارسوا هذا العمل فعلًا، وأطلقوا أنظمة في بيئات الإنتاج لدى الشركات أدناه. ' +
      'رأينا مهندسين مبتدئين متميزين يدخلون سوق العمل من دون أن يسبق لهم فحص عمل وكيل ذكاء اصطناعي ولو مرة. لذلك يحاكي كل مستوى هنا موقفًا تعلّمه أحدنا أثناء العمل.',
    /* 3-10 take the broken plural (`5 شركات`); 11 and up take the singular
       accusative (`11 شركة`), which is the case for the whole roster. */
    companies: (n: number) => `${num(n, 'ar')} ${n >= 3 && n <= 10 ? 'شركات' : 'شركة'}`,
    rosterLabel: 'شركات عمل فيها الفريق',
    note: 'هذه جهات عمل سابقة وليست جهات راعية أو مؤيدة للمنتج. لا تربط أيًا منها علاقة بـ EngineerOS.',
  },

  strip: {
    label: 'ما هذا',
    figures: (levels: number, worlds: number): Array<{ term: string; value: string }> => [
      { term: 'المستويات', value: num(levels, 'ar') },
      { term: 'الفصول', value: num(worlds, 'ar') },
      { term: 'خوادم مطلوبة لكل متعلّم', value: num(0, 'ar') },
      /* `~` before a number is the one value here that bidi can turn around:
         isolated, it stays on the left of the 10 where it belongs. */
      { term: 'أول زيارة حتى سطر الأوامر', value: ltr('~20s') },
    ],
  },

  proof: {
    label: 'لماذا يمكنك الوثوق به',
    title: 'تحقق من كل ادعاء بنفسك.',
    lede: (command: ReactNode) => [
      'ما يلي حقائق عن طريقة بناء المنتج، لا وعود تسويقية. افتح المستوى 0 وتحقق بنفسك. ابدأ بالأمر ',
      command,
      '.',
    ],
    claims: {
      linux: {
        title: 'Linux حقيقي، داخل تبويبك.',
        body:
          'يشغّل v86 نظام Alpine على معمارية x86 داخل WebAssembly. تعمل بداخله أدوات bash و Git و`node --test` فعلًا. ' +
          `ليس الـ ${ltr('terminal')} سيناريو مسجلًا، ولا نظام التحكم بالإصدارات نسخة مقلدة؛ لذلك تعمل الأوامر هنا كما تعمل في أي بيئة Linux حقيقية، حتى لو لم نضعها ضمن الدروس.`,
      },
      graded: {
        title: 'تُقيَّم على الحالة التي تتركها.',
        body:
          'يفحص نظام التقييم حالة المستودع النهائية، لا المفاتيح التي ضغطتها. يمكن أن تجتاز مستوى الاستعادة باستخدام ' +
          `\`git reset --hard HEAD@{1}\` أو ${ltr('cherry-pick')} أو ${ltr('merge')}، لأن كلًا منها يترك المستودع في الحالة الصحيحة، بينما لا تكفي إعادة كتابة الملف يدويًا. لا يوجد أمر سري عليك تخمينه. لذلك استخدم في المستويات أي أداة تريدها، بما فيها الذكاء الاصطناعي: تُقيَّم على ما تتركه خلفك، لا على الطريقة التي وصلت بها. ولا تقول محطة إن نموذجًا فشل فيها إلا بعد قياس ذلك: النموذج يعمل وحده ثلاث مرات ويفشل في كل مرة، وعندها تسمّي النموذج وإصداره بالضبط واليوم.`,
      },
      tested: {
        title: 'المستويات تُختبر كما تُختبر البرمجيات.',
        body:
          'يأتي كل مستوى مع حل مرجعي وحالات للأخطاء التي يقع فيها المتعلمون فعلًا. تتأكد الاختبارات من أن نقطة البداية ' +
          'قابلة للحل ولم تُحل مسبقًا، وأن الحل المرجعي ينجح، وأن كل إجابة خاطئة تفشل في الأهداف المتوقعة تحديدًا. إذا اختل مستوى، يفشل البناء.',
      },
      /* `محطة` is the checkpoint, as every checkpoint title already writes it
         (strings/verification.ts), and `فحص` is the check, as the checkpoint's
         own sentences word it (strings/checkpoint.ts). Both are borrowed rather
         than re-coined: this page is where a stranger meets the claim first,
         and meeting it under a second name would read as a second thing. */
      nothing: {
        title: 'لا شيء تثبّته. مساحة عملك تعمل محليًا.',
        body:
          'تعمل الطرفية والمستودع والتقييم و Iris كلها داخل متصفحك. Iris مصمَّمة: تقترح أوامر كُتبت لكل مستوى، ' +
          'ولا يُرسَل شيء مما تكتبه إلى خدمة ذكاء اصطناعي. يلزمك حساب بعد المستويات الخمسة الأولى، وعندما ترسل محطة ' +
          'لفحص مستقل، تُرفع مساحة عمل تلك المحطة.',
      },
    },
    ctaNote: `المستوى 0 يحتاج إلى 4 دقائق في الـ ${ltr('terminal')}. بلا تسجيل أو بطاقة أو تثبيت.`,
  },
};

export const de: typeof en = {
  pedigree: {
    label: 'Wer es gebaut hat',
    title: 'Gebaut von Engineers, die den Job gemacht haben.',
    lede: 'EngineerOS wurde von aktiven Softwareentwicklerinnen und -entwicklern geschrieben – Leuten, die bei den Firmen unten Produktivsysteme ausgeliefert haben und zu viele gute Juniors ankommen sahen, die noch nie die Arbeit eines Agenten geprüft hatten. Jedes Level ist etwas, das eine oder einer von uns im Job lernen musste.',
    companies: (n) => `${num(n, 'de')} ${n === 1 ? 'Firma' : 'Firmen'}`,
    rosterLabel: 'Firmen, bei denen das Team gearbeitet hat',
    note: 'Namen von Arbeitgebern, keine Empfehlungen. Keine dieser Firmen ist mit EngineerOS verbunden.',
  },
  strip: {
    label: 'Was das ist',
    figures: (levels, worlds) => [
      { term: 'Level', value: num(levels, 'de') },
      { term: 'Kapitel', value: num(worlds, 'de') },
      { term: 'Server pro Person', value: num(0, 'de') },
      { term: 'Erster Besuch bis zum Prompt', value: '~20 s' },
    ],
  },
  proof: {
    label: 'Warum du ihm trauen kannst',
    title: 'Glaubwürdigkeit, die du selbst prüfen kannst.',
    lede: (command) => [
      'Alles unten ist eine Tatsache darüber, wie das gebaut ist, kein Versprechen, wie es sich anfühlt. Öffne Level 0 und prüf es nach – ',
      command,
      ' ist ein guter erster Befehl.',
    ],
    claims: {
      linux: {
        title: 'Ein echtes Linux, in deinem Tab.',
        body: 'Alpine auf x86, emuliert von v86 in WebAssembly. Deine bash, dein git und dein `node --test` laufen alle darin. Es gibt kein geskriptetes Terminal und keine simulierte Versionskontrolle – ein Befehl funktioniert hier aus demselben Grund wie in einer Shell, auch die, an die wir nie gedacht haben.',
      },
      graded: {
        title: 'Bewertet wird der Zustand, den du hinterlässt.',
        body: 'Die Bewertung liest das Repository, nie deine Tastenanschläge. `git reset --hard HEAD@{1}`, ein Cherry-Pick und ein Merge bestehen alle das Wiederherstellungs-Level, weil alle drei das Repo korrekt hinterlassen – die Datei von Hand neu zu tippen nicht. Es gibt keinen erwarteten Befehl, den man erraten müsste. Nimm also in den Levels jedes Werkzeug, das du willst, KI eingeschlossen: Bewertet wird, was du hinterlässt, nicht, wie du dorthin gekommen bist. Ein Checkpoint sagt erst dann, dass ein Modell an ihm gescheitert ist, wenn das gemessen wurde – das Modell allein, dreimal, jedes Mal gescheitert – und nennt dann das Modell, seine genaue Version und den Tag.',
      },
      tested: {
        title: 'Level werden getestet wie Software.',
        body: 'Jedes Level kommt mit einer Referenzlösung und den falschen Lösungen, die Leute tatsächlich versuchen. Die Testsuite beweist, dass jeder Ausgangszustand lösbar, aber nicht schon gelöst ist, dass die Lösung besteht und dass jede falsche Antwort genau an den Zielen scheitert, an denen sie scheitern soll. Ein kaputtes Level ist ein roter Build.',
      },
      nothing: {
        title: 'Nichts zu installieren. Dein Arbeitsbereich läuft lokal.',
        body: 'Dein Terminal, dein Repository, die Bewertung und Iris laufen alle in deinem Browser. Iris ist entworfen: Sie schlägt Befehle vor, die für jedes Level geschrieben wurden, und nichts, was du tippst, wird an einen KI-Dienst gesendet. Nach den ersten fünf Leveln brauchst du ein Konto. Wenn du einen Checkpoint zur Prüfung einreichst, wird dessen Arbeitsbereich hochgeladen.',
      },
    },
    ctaNote: 'Level 0 sind vier Minuten am Terminal. Keine Anmeldung, keine Karte, nichts zu installieren.',
  },
};

export const ja: typeof en = {
  pedigree: {
    label: '作った人たち',
    title: '現場を経験したエンジニアが作りました。',
    lede: 'EngineerOSは、現役のソフトウェアエンジニアが書きました。下に挙げた企業で本番システムをリリースしてきた人たちであり、エージェントの仕事を一度も確かめたことがないまま現場に来る優秀なジュニアを、何人も見てきた人たちです。どのレベルも、私たちの誰かが実務で身につける必要があったことです。',
    companies: (n: number) => `${num(n, 'ja')}社`,
    rosterLabel: 'チームが所属していた企業',
    note: '過去の勤務先の名前であり、推奨ではありません。これらの企業はいずれもEngineerOSとは無関係です。',
  },
  strip: {
    label: 'EngineerOSとは',
    figures: (levels: number, worlds: number) => [
      { term: 'レベル', value: num(levels, 'ja') },
      { term: '章', value: num(worlds, 'ja') },
      { term: '1人あたりのサーバー', value: num(0, 'ja') },
      { term: '初回訪問からプロンプトまで', value: '約20秒' },
    ],
  },
  proof: {
    label: '信頼できる理由',
    title: '自分で確かめられる信頼性。',
    lede: (command: ReactNode) => [
      '以下はすべて、EngineerOSがどう作られているかという事実であり、使い心地についての約束ではありません。レベル0を開いて、どれでも確かめてみてください。最初のコマンドには',
      command,
      'がぴったりです。',
    ],
    claims: {
      linux: {
        title: 'あなたのタブの中で動く、本物のLinux。',
        body: 'x86上のAlpineを、v86がWebAssemblyでエミュレートしています。あなたのbash、git、`node --test` は、すべてその中で動きます。台本どおりに動くだけのターミナルも、模擬のバージョン管理もありません。だから、私たちが想定していなかったコマンドも含めて、シェルで動くのと同じ理由でここでも動きます。',
      },
      graded: {
        title: '採点されるのは、あなたが残した状態です。',
        body: '採点が読むのはリポジトリであり、キー入力ではありません。`git reset --hard HEAD@{1}`、チェリーピック、マージのどれでも復旧レベルをクリアできます。どれもリポジトリを正しい状態に残すからです。一方、ファイルを手で打ち直してもクリアできません。当てずっぽうで探すような「正解のコマンド」はありません。ですからレベルでは、AIを含めどんなツールを使っても構いません。採点されるのは、どうやって作ったかではなく、何を残したかです。チェックポイントが「モデルが失敗した」と書くのは、実際に測定したあとだけです。モデルが単独で3回挑戦して毎回失敗した場合に限り、そのモデル名、正確なバージョン、日付を明記します。',
      },
      tested: {
        title: 'レベルはソフトウェアと同じようにテストされています。',
        body: 'どのレベルにも、模範解答と、人が実際に試しがちな誤答が付いています。テストスイートは、どの初期状態も解けること、ただし最初から解かれていないこと、模範解答が合格すること、そして各誤答がまさに想定した目標で失敗することを証明します。壊れたレベルは、赤いビルドです。',
      },
      nothing: {
        title: 'インストールは不要。作業環境はローカルで動きます。',
        body: 'ターミナル、リポジトリ、採点、そしてIrisは、すべてあなたのブラウザーの中で動きます。Irisは設計されたエージェントです。レベルごとに書かれたコマンドを提案し、あなたが入力した内容がAIサービスに送られることはありません。アカウントが必要になるのは最初の5レベルのあとで、検証のためにチェックポイントを提出すると、その作業環境がアップロードされます。',
      },
    },
    ctaNote: 'レベル0は、ターミナルで4分。登録も、カードも、インストールも要りません。',
  },
};
